/**
 * Drawer z kartą (blok "Pieczątka i podpis" na stronie produktu, "Drawer z kartą" sekcji – klasa drawer--stamp) na
 * telefonie. Safari na iPhonie chowa pasek nawigacji przy przewijaniu strony w dół, więc drawer otwarty w tym stanie ma
 * wysokość całego ekranu. Dotknięcie dołu ekranu (np. przycisku drawera) Safari zabiera na rozwinięcie paska, który
 * wtedy zasłania dół drawera – a strona jest zablokowana, więc nic się nie przesuwa. Dlatego:
 * - gdy przy otwartym drawerze pasek nagle się pojawi (widoczna część ekranu się zmniejszy), a przyciski drawera są
 *   pod nim, drawer płynnie i szybko przewija się na dół (followEnd) – przyciski stają nad paskiem,
 * - część drawera zasłonięta przez pasek staje się odstępem pod treścią (--stamp-drawer-viewport-cut, assets/theme.css),
 *   więc na końcu treści przyciski są nad paskiem, a tło drawera zostaje pod nim,
 * - treść przewinięta do końca zostaje przewinięta do końca także po zmianie wysokości (np. obrót telefonu).
 * Różne wersje Safari zgłaszają pojawienie się paska różnymi miarami, więc widoczna część ekranu to najmniejsza z nich:
 * visualViewport, window.innerHeight, wysokość drawera i 100dvh (niewidoczny element próbny).
 */
if (!window.stampDrawerViewport) {
  window.stampDrawerViewport = true;

  const viewport = window.visualViewport;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 999px)'); // przewijanie do przycisków tylko w układzie telefonu
  const drawers = new Map(); // otwarty drawer -> { base, body, probe, observer, onScroll, onUser, atEnd, height, visible, full, width, frame, followUntil }
  const TOOLBAR_MIN = 16; // px – mniejsza zmiana wysokości to nie pasek
  const FOLLOW_TAU = 25; // ms – tempo dojazdu do przycisków (ok. 95% drogi po 3 × tyle)
  const FOLLOW_TIME = 400; // ms – tyle po ostatniej zmianie paska przewijanie dalej trzyma się dołu drawera

  const isAtEnd = (body) => body.scrollHeight - body.scrollTop - body.clientHeight < 2;

  // Przewinięcie na dół: jedna animacja klatka po klatce, która cały czas zmierza do aktualnego końca treści. Pasek
  // wysuwa się krokami (a z nim rośnie odstęp pod treścią), więc koniec się przesuwa – animacja płynnie za nim podąża,
  // zamiast zaczynać od nowa. Dotknięcie lub kółko myszy ją przerywa.
  const followEnd = (state) => {
    state.followUntil = performance.now() + FOLLOW_TIME;

    if (state.frame) {
      return;
    }

    let last = performance.now();

    const step = (now) => {
      const body = state.body;
      const target = body.scrollHeight - body.clientHeight;
      const distance = target - body.scrollTop;
      const elapsed = Math.min(64, now - last);
      last = now;

      if (reducedMotion.matches || Math.abs(distance) <= 1) {
        body.scrollTop = target;
      } else {
        // Co najmniej 1px na klatkę – przeglądarka zaokrągla przewinięcie, a animacja nie może utknąć tuż przed celem
        const move = Math.max(1, Math.abs(distance) * (1 - Math.exp(-elapsed / FOLLOW_TAU)));
        body.scrollTop += Math.sign(distance) * Math.min(Math.abs(distance), move);
      }

      state.frame = now < state.followUntil || Math.abs(target - body.scrollTop) > 1 ? requestAnimationFrame(step) : null;
    };

    state.frame = requestAnimationFrame(step);
  };

  const stopFollow = (state) => {
    cancelAnimationFrame(state.frame);
    state.frame = null;
    state.followUntil = 0;
  };

  // Wysokość widocznej części ekranu od góry drawera. Przy powiększeniu palcami visualViewport jest celowo mniejszy –
  // wtedy go pomijamy.
  const visibleHeight = (state) => {
    const base = state.base.getBoundingClientRect();
    const fromViewport = viewport && viewport.scale < 1.01 ? viewport.offsetTop + viewport.height - base.top : Infinity;

    return Math.round(Math.min(fromViewport, window.innerHeight - base.top, base.height, state.probe.offsetHeight || Infinity));
  };

  const update = () => {
    drawers.forEach((state, drawer) => {
      const visible = visibleHeight(state);
      const cut = Math.max(0, Math.round(state.base.getBoundingClientRect().height) - visible);

      if (cut > 0) {
        drawer.style.setProperty('--stamp-drawer-viewport-cut', `${cut}px`);
      } else {
        drawer.style.removeProperty('--stamp-drawer-viewport-cut');
      }

      // Pełna wysokość (bez paska) – od nowa po zmianie szerokości (obrót telefonu)
      if (window.innerWidth !== state.width) {
        state.full = visible;
        state.visible = visible;
        state.width = window.innerWidth;
      }

      state.full = Math.max(state.full, visible);

      // Pasek właśnie się pojawia (widoczna część niższa od pełnej i dalej maleje – Safari wysuwa go animacją), a przyciski
      // są pod nim: przewinięcie na dół. Później, gdy pasek już stoi, przewijanie należy do użytkownika.
      const buttons = drawer.querySelector('.stamp-drawer-part--buttons');
      const hidden = buttons && buttons.getBoundingClientRect().bottom > state.base.getBoundingClientRect().top + visible;

      if (mobileQuery.matches && visible < state.full - TOOLBAR_MIN && visible < state.visible && (hidden || state.frame)) {
        followEnd(state);
      }

      state.visible = visible;
    });
  };

  const watch = (drawer) => {
    if (!drawer.classList?.contains('drawer--stamp') || drawers.has(drawer)) {
      return;
    }

    const base = drawer.shadowRoot?.querySelector('[part="base"]');
    const body = drawer.shadowRoot?.querySelector('[part="body"]');

    if (!base || !body) {
      return;
    }

    // Element próbny o wysokości 100dvh – zmienia się razem z paskiem Safari także wtedy, gdy drawer nie
    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position: fixed; top: 0; left: 0; width: 0; height: 100vh; height: 100dvh; visibility: hidden; pointer-events: none;';
    document.body.append(probe);

    const state = { base, body, probe, atEnd: false, height: null, visible: null, full: 0, width: null, frame: null, followUntil: 0 };
    state.onScroll = () => { state.atEnd = isAtEnd(body); };
    state.onUser = () => stopFollow(state); // użytkownik sam przewija – przewijanie na dół od razu się zatrzymuje

    state.observer = new ResizeObserver((entries) => {
      // Zmiana wysokości treści drawera: treść przewinięta do końca zostaje na końcu
      if (entries.some((entry) => entry.target === body)) {
        const height = body.clientHeight;

        if (state.height !== null && height !== state.height && state.atEnd) {
          body.scrollTop = body.scrollHeight;
        }

        state.height = height;
        state.atEnd = isAtEnd(body);
      }

      update();
    });

    body.addEventListener('scroll', state.onScroll, { passive: true });
    ['touchstart', 'wheel'].forEach((type) => body.addEventListener(type, state.onUser, { passive: true }));
    // border-box: odstęp pod treścią (--stamp-drawer-viewport-cut) nie zmienia tej wysokości, więc nie wywołuje obserwatora
    [body, base, probe].forEach((element) => state.observer.observe(element, { box: 'border-box' }));
    drawers.set(drawer, state);
  };

  // Zdarzenia motywu (DialogElement w assets/theme.js) nie bąbelkują – faza przechwytywania na dokumencie
  document.addEventListener('dialog:before-show', (event) => watch(event.target), true);
  document.querySelectorAll('.drawer--stamp[open]').forEach(watch); // drawer otwarty, zanim wczytał się ten plik

  document.addEventListener('dialog:after-hide', (event) => {
    const state = drawers.get(event.target);

    if (!state) {
      return;
    }

    state.observer.disconnect();
    state.body.removeEventListener('scroll', state.onScroll);
    ['touchstart', 'wheel'].forEach((type) => state.body.removeEventListener(type, state.onUser));
    stopFollow(state);
    state.probe.remove();
    event.target.style.removeProperty('--stamp-drawer-viewport-cut');
    drawers.delete(event.target);
  }, true);

  viewport?.addEventListener('resize', update);
  viewport?.addEventListener('scroll', update);
  window.addEventListener('resize', update);
}
